import { useEffect, useState } from 'react'
import SoundController from './play/soundchunked'

interface IWaveForm {
  sound: SoundController
  count?: number
  width?: number
  gap?: number
  height?: number
}

const factor = 12
const length = 18 - 8
const tanh = (x: number) => (Math.tanh(Math.pow(Math.abs(x), 0.8) * factor - factor / 2) + 1) / 2
const linear = (x: number, min: number, max: number) => Math.max(0, x - min) / (max - min)

const WaveForm = ({ sound, count = 32, width = 8, gap = 4, height = 20 }: IWaveForm) => {
  const [waveform, setWaveform] = useState<number[]>(Array(count).fill(0))
  const [audioContext, setAudioContext] = useState(sound.ctx)
  const [ts, setTs] = useState(0)

  useEffect(() => {
    const handleContextRefresh = () => {
      setAudioContext(sound.ctx)
      setTs((t) => t + 1)
    }

    sound.on('contextRefresh', handleContextRefresh)
    setAudioContext(sound.ctx)

    return () => {
      sound.off('contextRefresh', handleContextRefresh)
    }
  }, [sound])

  useEffect(() => {
    if (!audioContext?.ctx) return
    let analyser: AnalyserNode
    try {
      analyser = audioContext.createAnalyser()
      audioContext.destination.connect(analyser)
    } catch (e) {
      console.log(e)
      return
    }
    const st = Math.floor(count * 0.15)
    analyser.fftSize = count * 8
    analyser.minDecibels = -90
    analyser.maxDecibels = 0
    analyser.smoothingTimeConstant = 0.8

    const bufferLength = analyser.frequencyBinCount
    const data = new Float32Array(bufferLength)
    let animId: number

    const draw = () => {
      analyser.getFloatFrequencyData(data)
      const sl = Array.from(data).slice(st, st + count)
      const val = sl.map((x, i) => x * (1 - Math.pow(i * (length - i - 1), 0.4) / length / length))

      const res = val.map((i) => tanh(linear(i, -90, Math.min(0, Math.max(...val) + 4))))

      setWaveform(res)
      animId = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      analyser.disconnect()
      cancelAnimationFrame(animId)
    }
  }, [audioContext, count, ts])

  return (
    <div className="flex gap-[4px] items-center justify-start shrink-0" style={{ gap: `${gap}px` }}>
      {waveform.map((amplitude, index) => (
        <div
          key={index}
          className="bg-purple-500 w-[8px] h-[20px] rounded-[100px]"
          style={{ width: `${width}px`, height: `${(height - width) * amplitude + width}px` }}
        ></div>
      ))}
    </div>
  )
}

export default WaveForm
